import React, { useCallback } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { generateFullName } from 'utils/generateFullNameUtils';
import { Tooltip } from 'primereact/tooltip';

import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { dateFilterOptions, taskPrioritySortList } from 'constants/dropdownOptions';
import { sortByKey } from 'utils/utils';
import { LoaderComponent } from 'shared/LoaderComponent';
import { useFilters } from '../../context/FilterContext';
import { useBoardData } from '../../context/BoardContext';
import { handleFilterResetUserContext } from 'common/pagination';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useBoardSection } from 'modules/TasksV2/context';
import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';

function Filter() {
  const userContext = useUserDetailsContext();
  const { isGlobal } = useBoardSection();
  // Get filter state and actions from FilterContext
  const {
    searchInput, // Use searchInput for the input field (immediate updates)
    practiceArea,
    assignedTo,
    priority,
    setPriority,
    sortType,
    dateFilter,
    setSearchInput, // Use setSearchInput to update search
    setPracticeArea,
    setAssignedTo,
    setSortType,
    setDateFilter,
    clearFilters,
  } = useFilters();

  // Get board data (practiceAreaList, userList, etc.)
  const { practiceAreaList, isPracticeAreaLoading, userList, isLoadingUsers, clientList, isLoadingClients } = useBoardData();

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      const filterObj = { ...(userContext?.userDetails?.user_preferences?.filters?.tasks || {}), [name]: value };
      switch (name) {
        case 'keyword':
          setSearchInput(value); // Update search input (debouncing handled in FilterContext)
          break;
        case 'assigned_to':
          setAssignedTo(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        case 'practice_area':
          setPracticeArea(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        case 'sort_by':
          setSortType(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        case 'priority':
          setPriority(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        case 'date':
          setDateFilter(value);
          isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, filterObj, true);
          break;
        default:
          break;
      }
    },
    [setSearchInput, setAssignedTo, setPracticeArea, setSortType, setDateFilter, userContext?.userDetails?.user_preferences?.filters?.tasks]
  );

  const resetFilters = useCallback(() => {
    clearFilters();
    const resetFilterObj = {
      assigned_to: null,
      practice_area: null,
      priority: null,
      date: null,
    };
    isGlobal && handleFilterResetUserContext(userContext, 'filters', 'tasks', null, resetFilterObj, true);
  }, [clearFilters]);

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {generateFullName(option)}
      </>
    );
  };

  return (
    <div className="filter-wrap d-flex flex-wrap">
      {isGlobal && (
        <div className="p-2">
          <span className="p-input-icon-left input-shadow">
            <i className="icon-search F-size16" />
            <TextSnippetWrapper
              type="text"
              placeholder="Search By Task Name"
              name="keyword"
              value={searchInput}
              onChange={handleFilterChange}
              className="p-inputtext p-component input-search autowidth"
            />
          </span>
        </div>
      )}
      <div className="d-flex align-items-center filter-wraper p-2">
        <span className="me-2 text-bold black-600  filter-label py-0">Filters: </span>
        <div className="d-flex flex-column me-2 task-filter py-0">
          {isGlobal ? (
            <MultiSelect
              name="assigned_to"
              value={assignedTo}
              onChange={handleFilterChange}
              options={sortByKey(userList, 'first_name') ?? []}
              optionLabel={(e) => generateFullName(e)}
              isSearchable
              placeholder="Select Assignee"
              className="w-100 input-shadow input-width input-height"
              optionValue="firm_user_id"
              filter
              filterPlaceholder="Search"
              resetFilterOnHide
              itemTemplate={assignToItemTemplate}
              panelFooterTemplate={isLoadingUsers ? LoaderComponent : null}
              dropdownIcon={isLoadingUsers ? 'pi pi-spinner pi-spin' : undefined}
            />
          ) : (
            <MultiSelect
              value={assignedTo}
              options={sortByKey(clientList, 'first_name') ?? []}
              onChange={handleFilterChange}
              filterBy="first_name,last_name"
              placeholder="Select Users"
              filter
              className="searchable-dropdown input-shadow"
              itemTemplate={assignToItemTemplate}
              maxSelectedLabels={1}
              showSelectAll={false}
              optionValue={'firm_user_id'}
              optionLabel={(e) => generateFullName(e)}
              name="assigned_to"
              showClear
              panelFooterTemplate={isLoadingClients ? LoaderComponent : null}
              dropdownIcon={isLoadingClients ? 'pi pi-spinner pi-spin' : undefined}
              resetFilterOnHide
            />
          )}
        </div>
        {isGlobal && (
          <div className="d-flex flex-column me-2 task-filter py-0">
            <Dropdown
              name="practice_area"
              value={practiceArea}
              placeholder="Select Practice Area"
              options={practiceAreaList?.filter((item) => item.status === 'ACTIVE') ?? []}
              filter
              filterPlaceholder="Search"
              optionLabel="practice_area_name"
              optionValue="practice_area_id"
              className="w-100 input-shadow autowidth input-height"
              onChange={handleFilterChange}
              resetFilterOnHide
              panelFooterTemplate={isPracticeAreaLoading ? LoaderComponent : null}
            />
          </div>
        )}
        <PriorityDropdown value={priority} onChange={handleFilterChange} name="priority" showClear className="me-2" />
        <div className="d-flex flex-column me-2 task-filter py-0">
          <Dropdown
            value={dateFilter}
            options={dateFilterOptions}
            onChange={handleFilterChange}
            placeholder="By Due Date"
            name="date"
            showClear
          />
        </div>
        <div className="d-flex flex-column me-2 task-filter py-0">
          <Dropdown
            name="sort_by"
            value={sortType}
            placeholder="Sort by"
            options={taskPrioritySortList}
            optionLabel="label"
            optionValue="value"
            onChange={handleFilterChange}
            resetFilterOnHide
          />
        </div>
        <div className="d-flex pointer align-items-center p-2">
          <i className="fas fa-sync-alt" onClick={resetFilters} />
          <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
        </div>
      </div>
    </div>
  );
}

// Memoize to prevent unnecessary rerenders
export default React.memo(Filter);
